// src/components/admin/goal/steps/Step1.js

import {
  Typography,
  Alert,
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { NumericFormat } from "react-number-format";
import StepNavigation from "./StepNavigation";

export default function Step1({ onBack, onNext, goal, setGoal, activeStep }) {
  const theme = useTheme();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 6 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Período da Meta
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Selecione abaixo o intervalo em que sua meta estará ativa.
        </Typography>

        <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }}>
          Ao alterar o intervalo de dias, os valores diários serão redistribuídos
          igualmente entre todos os dias da meta.
        </Alert>

        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Grid container spacing={3}>
            {/* Mês de referência */}
            <Grid item xs={12} md={6}>
              <DatePicker
                views={["year", "month"]}
                label="Mês de referência"
                value={
                  goal.period ? dayjs(`${goal.period}-01`) : dayjs()
                }
                onChange={(newValue) =>
                  setGoal((prev) => ({
                    ...prev,
                    period: newValue.format("YYYY-MM"),
                  }))
                }
                format="MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {/* Tipo */}
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <TextField
                select
                label="Tipo"
                value={goal.type || "amount"}
                onChange={(e) =>
                  setGoal((prev) => ({ ...prev, type: e.target.value }))
                }
                fullWidth
                sx={{ width: "245px" }}
              >
                <MenuItem value="amount">Valor (R$)</MenuItem>
                <MenuItem value="quantity">Quantidade</MenuItem>
              </TextField>
            </Grid>

            {/* Início */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Início da meta"
                value={
                  goal.start_date
                    ? dayjs(goal.start_date)
                    : dayjs().startOf("month")
                }
                onChange={(newValue) =>
                  setGoal((prev) => ({
                    ...prev,
                    start_date: newValue.format("YYYY-MM-DD"),
                  }))
                }
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {/* Final */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2}>
                <DatePicker
                  label="Final da meta"
                  value={
                    goal.end_date
                      ? dayjs(goal.end_date)
                      : dayjs().endOf("month")
                  }
                  onChange={(newValue) =>
                    setGoal((prev) => ({
                      ...prev,
                      end_date: newValue.format("YYYY-MM-DD"),
                    }))
                  }
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
                {/* Quantidade de dias */}
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  whiteSpace="nowrap"
                >
                  {goal.start_date && goal.end_date
                    ? `${dayjs(goal.end_date).diff(
                        dayjs(goal.start_date),
                        "day"
                      ) + 1} dias`
                    : "-"}
                </Typography>
              </Box>
            </Grid>

            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                label="Nome da meta"
                value={goal.name || ""}
                onChange={(e) =>
                  setGoal((prev) => ({ ...prev, name: e.target.value }))
                }
                fullWidth
                sx={{ width: "245px" }}
              />
            </Grid>

            {/* Meta */}
            <Grid item xs={12}>
              <NumericFormat
                value={goal.target || ""}
                customInput={TextField}
                label="Valor da meta"
                fullWidth
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                onValueChange={({ floatValue }) =>
                  setGoal((prev) => ({
                    ...prev,
                    target: floatValue || 0,
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R$</InputAdornment>
                  ),
                }}
                sx={{ width: "245px" }}
              />
            </Grid>
          </Grid>
        </Paper>

        <StepNavigation activeStep={activeStep} onBack={onBack} onNext={onNext} />
      </Box>
    </LocalizationProvider>
  );
}
